import { env } from 'next-runtime-env';
import { v4 as uuidv4 } from 'uuid';
import { ErrorCategory, ResponseError } from '@/lib/exceptions';
import { logger } from '@/lib/logger';

/**
 * Server-side base service for direct backend API communication
 * Used in middleware and server components that need to call microservices directly
 */

export type BackendHttpRequestInput = {
  api?: 'funding' | 'authorization' | 'measurement' | 'reporting' | 'deals' | 'collateral' | 'notification';
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  body?: any;
  accessToken: string;
  correlationId?: string;
};

export type BackendHttpRequest = {
  method: string;
  headers: HeadersInit;
  body?: string;
  duplex?: 'half';
};

const getBaseApiUrl = (api: string): string => {
  switch (api) {
    case 'funding':
      return env('AGA_SERVICE_FUNDING_URL') || '';
    case 'authorization':
      return env('AGA_SERVICE_AUTHORIZATION_URL') || '';
    case 'measurement':
      return env('AGA_SERVICE_MEASUREMENT_URL') || '';
    case 'reporting':
      return env('AGA_SERVICE_REPORTING_URL') || '';
    case 'deals':
      return env('AGA_SERVICE_DEAL_URL') || '';
    case 'collateral':
      return env('AGA_SERVICE_COLLATERAL_URL') || '';
    case 'notification':
      return env('AGA_SERVICE_NOTIFICATION_URL') || '';
    default:
      return '';
  }
};

/**
 * Stringifies request body for HTTP requests.
 * Handles the case where Next.js API routes parse JSON bodies into objects.
 * @param body - The request body (can be string or object)
 * @param method - HTTP method
 * @returns Stringified body or undefined
 */
export const stringifyRequestBody = (body: any, method: string): string | undefined => {
  if (body && method !== 'GET') {
    return typeof body === 'string' ? body : JSON.stringify(body);
  }
  return undefined;
};

class BackendBaseService {
  /**
   * Make direct HTTP request to backend microservice
   * Used for server-side calls (middleware, server components)
   */
  protected async backendHttpRequest<T>({
    api = 'funding',
    method,
    path,
    body,
    accessToken,
    correlationId = uuidv4(),
  }: BackendHttpRequestInput): Promise<T> {
    const baseUrl = getBaseApiUrl(api);
    const requestPath = `${baseUrl}${path}`;

    console.log(`🔗 [BackendService] ${method} ${requestPath}`);

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'x-correlation-id': correlationId,
    };

    const init: BackendHttpRequest = {
      method,
      headers,
      duplex: 'half',
    };

    init.body = stringifyRequestBody(body, method);

    try {
      const request = new Request(requestPath, init);
      const response = await fetch(request);

      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        if (contentType && contentType.includes('application/json')) {
          const responseData = await response.json();
          throw new ResponseError(responseData.message || 'Something went wrong', response.status || 500, {
            cause: responseData,
            category:
              response.status === 401
                ? ErrorCategory.AUTHENTICATION
                : response.status === 403
                  ? ErrorCategory.AUTHORIZATION
                  : ErrorCategory.INTERNAL,
          });
        }

        throw new ResponseError('Something went wrong', response.status, {
          category: ErrorCategory.INTERNAL,
        });
      }

      if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
        return response as any;
      }

      return await response.json();
    } catch (error) {
      logger({
        service: api,
        level: 'ERROR',
        body: { url: requestPath, error, correlationId, context: 'BackendBaseService' },
      });
      throw error;
    }
  }

  /**
   * GET request to backend service
   */
  protected async get<T>({
    api = 'funding',
    path,
    accessToken,
    correlationId,
  }: Omit<BackendHttpRequestInput, 'method' | 'body'>): Promise<T> {
    return this.backendHttpRequest<T>({
      api,
      method: 'GET',
      path,
      accessToken,
      correlationId,
    });
  }

  /**
   * POST request to backend service
   */
  protected async post<T>({
    api = 'funding',
    path,
    body,
    accessToken,
    correlationId,
  }: Omit<BackendHttpRequestInput, 'method'>): Promise<T> {
    return this.backendHttpRequest<T>({
      api,
      method: 'POST',
      path,
      body,
      accessToken,
      correlationId,
    });
  }

  /**
   * PUT request to backend service
   */
  protected async put<T>({
    api = 'funding',
    path,
    body,
    accessToken,
    correlationId,
  }: Omit<BackendHttpRequestInput, 'method'>): Promise<T> {
    return this.backendHttpRequest<T>({
      api,
      method: 'PUT',
      path,
      body,
      accessToken,
      correlationId,
    });
  }

  /**
   * PATCH request to backend service
   */
  protected async patch<T>({
    api = 'funding',
    path,
    body,
    accessToken,
    correlationId,
  }: Omit<BackendHttpRequestInput, 'method'>): Promise<T> {
    return this.backendHttpRequest<T>({
      api,
      method: 'PATCH',
      path,
      body,
      accessToken,
      correlationId,
    });
  }

  /**
   * DELETE request to backend service
   */
  protected async delete<T>({
    api = 'funding',
    path,
    accessToken,
    correlationId,
  }: Omit<BackendHttpRequestInput, 'method' | 'body'>): Promise<T> {
    return this.backendHttpRequest<T>({
      api,
      method: 'DELETE',
      path,
      accessToken,
      correlationId,
    });
  }
}

export default BackendBaseService;
